import { getFullVelocityUrl, getPageId, handleResponse } from '@/Helper';
import { enrollmentApi } from '@/api/';
import { HelpFactoryComponent } from '@/components/common';
import { PIVCardInfo, PIVHeader } from '@/components/enrollment';
import { PersonDetailModel, PivValidation } from '@/model/EnrollmentModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { PIVEnrollmentAction } from '@/model/WebSocketModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setSocketPIVEnrollmentResponseAction } from '@/store/common/actions';
import { selectPIVEnrollmentAction } from '@/store/common/selectors';
import {
	setDisplayAddViaSmartCard,
	setFetchingScanData,
	setHavePersonChanged,
	setIsEnrollmentTabLoading,
	setPersonInformationId,
} from '@/store/enrollment/actions';
import { selectFetchingScanData, selectPersonId, selectPersonPIVFullName } from '@/store/enrollment/selectors';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Breadcrumb, BreadcrumbProps, Divider, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { enrollmentGoBackTextId } from '../helper';
import styles from './pivContainer.module.scss';

const velocityUrl = getFullVelocityUrl();
const downloadPivReaderUrl = `${velocityUrl}Enrollment/DownloadWebPIVReader`;
const enrollmentManagerUrl = `${velocityUrl}Enrollment/EnrollmentManager`;
const topicId: string = 'InstallingTheVelocityWebPIVReaderSoftware';

type Props = {
	goBackText: string;
	setUpdatePivPersonData?: (newValue: PersonDetailModel) => void;
};

const PIVContainer: React.FC<Props> = ({ goBackText, setUpdatePivPersonData }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [validationResult, setValidationResult] = useState<PivValidation>(null);

	const personId: number = useStoreSelector<number>(selectPersonId);
	const fullName: string = useStoreSelector<string>(selectPersonPIVFullName);
	const fetchingScanData: boolean = useStoreSelector<boolean>(selectFetchingScanData);
	const pivEnrollmentAction: PIVEnrollmentAction = useStoreSelector<PIVEnrollmentAction>(selectPIVEnrollmentAction);
	//#endregion

	useEffect(() => {
		if (pivEnrollmentAction) {
			if (pivEnrollmentAction === PIVEnrollmentAction.NewEntry) {
				const pageId = getPageId();
				enrollmentApi
					.retrievePivEnrollment(pageId)
					.then(response => {
						if (!handleResponse(response)) {
							const data = response.Entity;
							//Status = 0 means there wasn't any record in the DB and we need to keep "long pooling"
							if (data.FetchStatus === 0) {
								Logger.writeErrorLog(`We got an null value from the DB while retrieving using pageId = [${pageId}]`);
							}
							//Status = 1 means there was an update and we need to redirect to enrollment screen
							else if (data.FetchStatus === 1) {
								window.location.href = enrollmentManagerUrl;
							} else {
								batch(() => {
									//Status 2 means we had a record on the DB and we will process that record to re-render screen
									const hasChange = data.FetchStatus === 2;
									if (hasChange) {
										handleFetchScanData();
									}
									setIsLoading(false);
									setValidationResult(data);
								});
							}
						}
					})
					.catch(e => Logger.writeErrorLog(e))
					.finally(() => {
						dispatch(setSocketPIVEnrollmentResponseAction(PIVEnrollmentAction.None));
					});
			} else if (pivEnrollmentAction === PIVEnrollmentAction.ForceDownload) {
				//Status = 1 means there was an update and we need to redirect to enrollment screen
				window.location.href = enrollmentManagerUrl;
			}
		}
	}, [pivEnrollmentAction]);

	const handleFetchScanData = () => {
		dispatch(setFetchingScanData(false));
	};

	const handleReturnToEnrollmentInAddPIV = () => {
		batch(() => {
			dispatch(setFetchingScanData(false));
			dispatch(setDisplayAddViaSmartCard(false));
		});
	};

	const handleReturnToEnrollmentInUpdatePIV = () => {
		batch(() => {
			dispatch(setFetchingScanData(false));
			dispatch(setPersonInformationId(0));
			dispatch(setIsEnrollmentTabLoading(false));
			dispatch(setDisplayAddViaSmartCard(false));
			dispatch(setHavePersonChanged(false));
		});
	};

	const handleOnClickBack = e => {
		if (personId !== 0) {
			handleReturnToEnrollmentInUpdatePIV();
		} else {
			handleReturnToEnrollmentInAddPIV();
		}
	};

	const handleOnGoBackToPerson = e => {
		handleReturnToEnrollmentInAddPIV();
	};

	const breadCrumbTitle: BreadcrumbProps['items'] =
		personId === 0
			? [
					{
						title: _('AddPersonViaSmartCard'),
					},
			  ]
			: [
					{
						title: (
							<a onClick={handleOnGoBackToPerson}>
								<label>{fullName}</label>
							</a>
						),
					},
					{
						title: _('UpdatePersonViaSmartCard'),
					},
			  ];

	const showCreatePerson = !fetchingScanData && validationResult && validationResult.CertCheckResponse.OKToAcceptCard;

	const items: BreadcrumbProps['items'] = [
		{
			title: (
				<a onClick={handleOnClickBack}>
					<ArrowLeftOutlined /> <label id={enrollmentGoBackTextId}>{goBackText}</label>
				</a>
			),
		},
		...breadCrumbTitle,
	];

	return (
		<div className={styles.container}>
			<div>
				<Breadcrumb items={items} separator='/' />
			</div>
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className={styles.spinContainer}>
				<div className={styles.modalMock}>
					<p className={styles.modalTitle}>{_('VerifySmartCardInformation')}</p>
					<Divider className={styles.divider} />
					<PIVHeader
						setIsLoading={setIsLoading}
						validationResult={validationResult}
						showCreatePerson={showCreatePerson}
						downloadPivReaderUrl={downloadPivReaderUrl}
						setUpdatePivPersonData={setUpdatePivPersonData}
					/>
					<Divider className={styles.divider} />
					{!fetchingScanData && !validationResult && (
						<div className={styles.optionsContainer}>
							<div className={styles.actions}>
								<span>
									{_('EnrollmentPIVReaderDownload')} <a href={downloadPivReaderUrl}>{_('DownloadNow')}</a>
								</span>
								<HelpFactoryComponent helpFeature={HelpFeature.Enrollment} topicId={topicId} />
							</div>
						</div>
					)}
					{fetchingScanData && <span className={styles.instructions}>{_('EnrollmentInstructions')}</span>}
					{!fetchingScanData && validationResult && <PIVCardInfo validationResult={validationResult} />}
				</div>
			</Spin>
		</div>
	);
};

export { PIVContainer };
